import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  MdDashboard, 
  MdPeople, 
  MdReceipt, 
  MdNotifications, 
  MdBarChart, 
  MdSettings,
  MdBrightness4,
  MdHeadset,
  MdBusiness,
  MdLogout
} from 'react-icons/md';
import { configuracionAPI } from '../../services/api';
import SupportModal from '../SupportModal';
import { useAuth } from '../../context/AuthContext';

const SidebarContainer = styled.aside`
  width: 250px;
  height: 100vh;
  background-color: ${props => props.theme.colors.sidebarBg};
  box-shadow: ${props => props.theme.shadows.md};
  border-bottom-right-radius: 30px;
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 200px;
  }
`;

const Logo = styled.div`
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  
  h2 {
    font-size: ${props => props.theme.fontSizes.md};
    color: ${props => props.theme.colors.textPrimary};
    margin: 0;
    text-align: center;
  }
`;

const LogoImage = styled.div`
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  
  svg {
    font-size: 2.5rem;
    color: ${props => props.theme.colors.primary};
  }
`;

const Nav = styled.nav`
  flex: 1;
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.sidebarText};
  font-size: ${props => props.theme.fontSizes.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  transition: ${props => props.theme.transitions.fast};
  border-left: 3px solid transparent;
  height: 50px;

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.sidebarHover};
    color: ${props => props.theme.colors.primary};
    scale:1.025;
  }

  &.active {
    background-color: ${props => props.theme.colors.sidebarHover};
    color: ${props => props.theme.colors.sidebarActive};
    border-left-color: ${props => props.theme.colors.sidebarActive};
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const Footer = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const FooterButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
  background: transparent;
  color: ${props => props.theme.colors.sidebarText};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  transition: ${props => props.theme.transitions.fast};
  border: none;
  cursor: pointer;
  height: 30px;

  svg {
    font-size: 1rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.sidebarHover};
    color: ${props => props.theme.colors.primary};
  }

  &.logout {
    color: #e74c3c;
    
    &:hover {
      background-color: #fee;
      color: #c33;
    }
  }
`;

const FooterLinks = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};

  a {
    color: ${props => props.theme.colors.textSecondary};
    
    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const Sidebar = () => {
  const [config, setConfig] = useState(null);
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await configuracionAPI.getConfiguracion();
      setConfig(response.data.data);
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    }
  };

  const handleLogout = () => {
    if (window.confirm('¿Estás seguro de que deseas cerrar sesión?')) {
      logout();
      navigate('/login');
    }
  };

  return (
    <>
      <SidebarContainer>
      <Logo>
        <LogoImage>
          {config?.logo_url ? (
            <img src={config.logo_url} alt={config.nombre_empresa || 'Logo'} />
          ) : (
            <MdBusiness />
          )}
        </LogoImage>
        <h2>{config?.nombre_empresa || 'Payment Manager'}</h2>
      </Logo>
      
      <Nav>
        <NavItem to="/" end>
          <MdDashboard />
          <span>Inicio</span>
        </NavItem>
        
        <NavItem to="/clientes">
          <MdPeople />
          <span>Clientes</span>
        </NavItem>
        
        <NavItem to="/recibos">
          <MdReceipt />
          <span>Recibos y Facturas</span>
        </NavItem>
        
        <NavItem to="/alertas">
          <MdNotifications />
          <span>Alertas y Notificaciones</span>
        </NavItem>
        
        <NavItem to="/reportes">
          <MdBarChart />
          <span>Reportes y Analítica</span>
        </NavItem>
        
        <NavItem to="/configuracion">
          <MdSettings />
          <span>Configuración</span>
        </NavItem>
      </Nav>
      
      <Footer>
        <FooterButton onClick={() => setSupportModalOpen(true)}>
          <MdHeadset />
          <span>Soporte</span>
        </FooterButton>

        <FooterButton className="logout" onClick={handleLogout}>
          <MdLogout />
          <span>Cerrar Sesión</span>
        </FooterButton>
        
        <FooterLinks>
          {user && <div style={{ textAlign: 'center', width: '100%', marginTop: '10px', fontSize: '0.8rem' }}>
            {user.nombre}
          </div>}
        </FooterLinks>
      </Footer>
    </SidebarContainer>

    <SupportModal 
      isOpen={supportModalOpen}
      onClose={() => setSupportModalOpen(false)}
    />
  </>
  );
};

export default Sidebar;
